import { View, TouchableOpacity, Text } from 'react-native';
import React, { useState, useCallback, useMemo, useEffect } from 'react';
import queryString from 'query-string';
import {
  useStaticChartData,
  allValuesZero,
  mapDataToChartFormat,
  useChartValue,
  getSoftColor,
  getMonochromaticColor,
} from './hook';
import { BarChart, LineChart, PieChart, RadarChart } from './RnCharts';
import { ICharts } from '@nocode/types';

const Charts: React.FC<ICharts> = (props) => {
  const {
    attributes,
    onPress,
    height,
    zIndex,
    dataBinding,
    width,
    opacity,
    componentData,
  } = props;
  console.log("===v4 charts", props);

  const [selectedPeriod, setSelectedPeriod] = useState<'weekly' | 'monthly'>(
    'weekly'
  );
  const [dateOffset, setDateOffset] = useState<number>(0);

  const search = queryString.parse(window?.location?.search);
  const isCanvas = !search?.target;
  const {
    hasLegend,
    gridLines,
    chartType,
    items,
    backgroundColor,
    textColor,
    // Line Chart specific attributes
    showLegend,
    legendPosition,
    showHorizontalGrid,
    showVerticalGrid,
    gridCount,
    gridLineColor,
    gridLineWidth,
    gridLineStyle,
    maxValue,
    xAxisLabel,
    yAxisLabel,
    xAxisField,
    yAxisField,
    showXAxisLabel,
    showYAxisLabel,
    // Multi Plot specific attributes
    xAxisType,
    xAxisMultiFields,
    xAxisRangeMin,
    xAxisRangeMax,
    xAxisDateField,
    xAxisDateStart,
    xAxisDateEnd,
    yAxisType,
    yAxisMultiFields,
    yAxisRangeMin,
    yAxisRangeMax,
    yAxisDateField,
    yAxisDateStart,
    yAxisDateEnd,
    // Filter button styling
    filterButtonActiveColor,
    filterButtonInactiveColor,
    filterButtonTextActiveColor,
    filterButtonTextInactiveColor,
    showDateFilter,
    // Pie Chart specific attributes
    pieShowLabels,
    pieShowPercentage,
    pieShowValues,
    pieLabelColor,
    pieLabelsOutside,
    pieShowLegend,
    pieLegendPosition,
    pieUseRandomColors,
    // Pie Chart childComponents
    label,
    value,
  } = attributes as any;

  const datasetMode = props.datasetMode || 'single';

  // Navigation functions
  const navigateLeft = useCallback(() => {
    const daysToMove = selectedPeriod === 'weekly' ? -7 : -30;
    setDateOffset((prev) => prev + daysToMove);
  }, [selectedPeriod]);

  const navigateRight = useCallback(() => {
    const daysToMove = selectedPeriod === 'weekly' ? 7 : 30;
    setDateOffset((prev) => prev + daysToMove);
  }, [selectedPeriod]);

  const togglePeriod = useCallback((period: 'weekly' | 'monthly') => {
    setSelectedPeriod(period);
    setDateOffset(0); // Reset to current period when switching
  }, []);

  // Check if x-axis or y-axis are date fields to automatically set grid count to 7
  const xAxisBinding = Array.isArray(xAxisField)
    ? xAxisField.find((item: any) => typeof item === 'object')
    : null;
  const yAxisBinding = Array.isArray(yAxisField)
    ? yAxisField.find((item: any) => typeof item === 'object')
    : null;

  const isXAxisDate =
    xAxisBinding &&
    (xAxisBinding.source?.dataType === 'date' ||
      xAxisBinding.source?.dataType === 'dateOnly' ||
      xAxisBinding.source?.type === 'dateTime');

  const isYAxisDate =
    yAxisBinding &&
    (yAxisBinding.source?.dataType === 'date' ||
      yAxisBinding.source?.dataType === 'dateOnly' ||
      yAxisBinding.source?.type === 'dateTime');

  // Get maxValue from componentData first, then fallback to attributes
  const resolvedMaxValue = componentData?.maxValue
    ? parseFloat(componentData.maxValue)
    : maxValue && typeof maxValue === 'object' && maxValue.parsedFormula
    ? parseFloat(maxValue.parsedFormula)
    : parseFloat(maxValue) || null;

  // Set grid count based on field type
  let dynamicGridCount = gridCount;
  let horizontalGridCount = 7; // Y-axis grid should be consistent for both weekly and monthly

  if (isXAxisDate || isYAxisDate) {
    // X-axis (vertical grid) varies by period, Y-axis (horizontal grid) stays consistent
    dynamicGridCount = selectedPeriod === 'weekly' ? 7 : 30;
    horizontalGridCount = 7; // Keep Y-axis grid consistent
  } else if (
    xAxisBinding &&
    (xAxisBinding.source?.dataType === 'number' ||
      xAxisBinding.source?.type === 'number')
  ) {
    // Number fields: use maxValue from componentData or config
    if (resolvedMaxValue && resolvedMaxValue > 0) {
      // Use resolved maxValue - don't factor in height, use fixed calculation
      dynamicGridCount = Math.min(
        20,
        Math.max(5, Math.ceil(resolvedMaxValue / 20))
      );
    } else if (dataBinding && dataBinding.length > 0) {
      const fieldId = xAxisBinding.source?.fieldId;
      const values = dataBinding
        .map((item: any) => parseFloat(item[fieldId]) || 0)
        .filter((val: number) => !isNaN(val) && val > 0);

      if (values.length > 0) {
        const dataMaxValue = Math.max(...values);
        dynamicGridCount = Math.min(
          10,
          Math.max(5, Math.ceil((dataMaxValue + 20) / 10))
        );
      } else {
        dynamicGridCount = 5; // Default for blank number fields
      }
    } else {
      dynamicGridCount = 5; // Default when no data
    }
  } else if (
    yAxisBinding &&
    (yAxisBinding.source?.dataType === 'number' ||
      yAxisBinding.source?.type === 'number')
  ) {
    // Y-axis number fields: use maxValue from componentData or config
    if (resolvedMaxValue && resolvedMaxValue > 0) {
      // Use resolved maxValue - don't factor in height, use fixed calculation
      dynamicGridCount = Math.min(
        20,
        Math.max(5, Math.ceil(resolvedMaxValue / 20))
      );
    } else if (dataBinding && dataBinding.length > 0) {
      const fieldId = yAxisBinding.source?.fieldId;
      const values = dataBinding
        .map((item: any) => parseFloat(item[fieldId]) || 0)
        .filter((val: number) => !isNaN(val) && val > 0);

      if (values.length > 0) {
        const dataMaxValue = Math.max(...values);
        dynamicGridCount = Math.min(
          10,
          Math.max(5, Math.ceil((dataMaxValue + 20) / 10))
        );
      } else {
        dynamicGridCount = 5; // Default for blank number fields
      }
    } else {
      dynamicGridCount = 5; // Default when no data
    }
  }

  // Convert grid line style to strokeDasharray
  const getStrokeDashArray = (style: string) => {
    switch (style) {
      case 'dashed':
        return '5,5';
      case 'dotted':
        return '2,2';
      default:
        return '';
    }
  };

  const chartConfig = useMemo(
    () => ({
      backgroundColor: backgroundColor || '#ffffff',
      backgroundGradientFrom: backgroundColor || '#ffffff',
      backgroundGradientTo: backgroundColor || '#ffffff',
      color: (opacity = 1) => textColor || `rgba(134, 65, 244, ${opacity})`,
      strokeWidth: 1,
      barPercentage: 0.5,
      useShadowColorFromDataset: false,
      decimalPlaces: 0,
      style: {
        borderRadius: 16,
      },
      propsForDots: {
        r: '6',
        strokeWidth: '1',
        stroke: textColor || '#8665F4',
      },
      propsForBackgroundLines: {
        strokeDasharray:
          chartType === 'line'
            ? getStrokeDashArray(gridLineStyle || 'solid')
            : '',
        stroke:
          chartType === 'line'
            ? showHorizontalGrid !== false
              ? gridLineColor || '#e3e3e3'
              : 'transparent'
            : gridLines?.enabled
            ? gridLines.color || '#e3e3e3'
            : 'transparent',
        strokeWidth: chartType === 'line' ? gridLineWidth || 1 : 1,
      },
      // Line Chart specific configuration - use datasetMode or auto-detect
      mode:
        props.datasetMode === 'multiple' ||
        props.datasetMode === 'multiplot' ||
        (props.datasets && props.datasets.length > 1) ||
        (props.dynamicFields &&
          props.dynamicFields.length > 0 &&
          props.datasetMode === 'multiplot')
          ? ('multiple' as const)
          : ('single' as const),
      showHorizontalGrid: showHorizontalGrid !== false,
      showVerticalGrid: showVerticalGrid || false,
      gridCount: dynamicGridCount, // X-axis grid count varies by period
      horizontalGridCount: horizontalGridCount, // Y-axis grid count consistent for legends
      multiAxis: {
        enabled: false,
      },
      legend: {
        enabled: showLegend !== false,
        position: legendPosition || 'bottom',
      },
      axisLabels: {
        x: {
          enabled: showXAxisLabel !== false,
          label: xAxisLabel || 'Time Period',
        },
        y: {
          enabled: showYAxisLabel !== false,
          label: yAxisLabel || 'Values',
        },
        yRight: {
          enabled: false,
          label: 'Secondary Values',
        },
      },
      // Multi Plot specific configuration
      multiPlot: {
        enabled: props.datasetMode === 'multiplot',
        xAxis: {
          type: xAxisType || 'field',
          fields: xAxisMultiFields || [],
          range: {
            min: xAxisRangeMin || 0,
            max: xAxisRangeMax || 100,
          },
          dateField: xAxisDateField,
          dateRange: {
            start: xAxisDateStart,
            end: xAxisDateEnd,
          },
        },
        yAxis: {
          type: yAxisType || 'field',
          fields: yAxisMultiFields || [],
          range: {
            min: yAxisRangeMin || 0,
            max: yAxisRangeMax || 100,
          },
          dateField: yAxisDateField,
          dateRange: {
            start: yAxisDateStart,
            end: yAxisDateEnd,
          },
        },
      },
    }),
    [
      backgroundColor,
      textColor,
      chartType,
      gridLineStyle,
      showHorizontalGrid,
      gridLineColor,
      gridLineWidth,
      gridLines,
      props.datasetMode,
      props.datasets,
      props.dynamicFields,
      showVerticalGrid,
      dynamicGridCount,
      horizontalGridCount,
      showLegend,
      legendPosition,
      showXAxisLabel,
      xAxisLabel,
      showYAxisLabel,
      yAxisLabel,
      xAxisType,
      xAxisMultiFields,
      xAxisRangeMin,
      xAxisRangeMax,
      xAxisDateField,
      xAxisDateStart,
      xAxisDateEnd,
      yAxisType,
      yAxisMultiFields,
      yAxisRangeMin,
      yAxisRangeMax,
      yAxisDateField,
      yAxisDateStart,
      yAxisDateEnd,
      selectedPeriod, // Add selectedPeriod as dependency
    ]
  );

  const filterControlsHeight = useMemo(() => {
    const shouldShowFilter =
      chartType === 'line' &&
      showDateFilter === true &&
      (isXAxisDate || isYAxisDate);
    return shouldShowFilter ? 48 : 0; // 32px button height + 16px padding
  }, [chartType, showDateFilter, isXAxisDate, isYAxisDate]);

  const legendHeight = useMemo(() => {
    if (showLegend === false) return 0;
    // Legend takes up space regardless of position
    return height < 300 ? 30 : 40;
  }, [showLegend, height]);

  const xAxisLabelHeight = useMemo(() => {
    if (showXAxisLabel === false) return 15; // Just for x-axis tick labels
    return height < 300 ? 35 : 45;
  }, [showXAxisLabel, height]);

  const commonProps = useMemo(
    () => ({
      width: width,
      height: height, // Use full height - let LineChart handle internal spacing
      chartConfig: chartConfig,
      bezier: chartType === 'line',
      hasFilterControls: filterControlsHeight > 0, // Pass filter info to chart
      style: {
        marginVertical: 8,
        borderRadius: 16,
      },
    }),
    [
      width,
      height,
      filterControlsHeight,
      legendHeight,
      xAxisLabelHeight,
      chartConfig,
      chartType,
    ]
  );

  // Filter controls component
  const FilterControls = useMemo(() => {
    const shouldShowFilter =
      chartType === 'line' &&
      showDateFilter === true &&
      (isXAxisDate || isYAxisDate);

    if (!shouldShowFilter) {
      return null;
    }

    const buttonHeight = 32;
    const controlsHeight = buttonHeight + 16;

    return (
      <View
        style={{
          flexDirection: 'row',
          alignItems: 'center',
          justifyContent: 'space-between',
          paddingHorizontal: 16,
          paddingVertical: 8,
          height: controlsHeight,
        }}
      >
        {/* Left Arrow */}
        <TouchableOpacity
          onPress={navigateLeft}
          style={{
            width: 32,
            height: buttonHeight,
            borderRadius: 6,
            backgroundColor: filterButtonInactiveColor || '#f0f0f0',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <Text
            style={{
              fontSize: 16,
              fontWeight: 'bold',
              color: filterButtonTextInactiveColor || '#666',
            }}
          >
            ←
          </Text>
        </TouchableOpacity>

        {/* Period Filter Buttons */}
        <View
          style={{
            flexDirection: 'row',
            backgroundColor: filterButtonInactiveColor || '#f0f0f0',
            borderRadius: 6,
            padding: 2,
          }}
        >
          <TouchableOpacity
            onPress={() => togglePeriod('weekly')}
            style={{
              paddingHorizontal: 16,
              paddingVertical: 6,
              borderRadius: 4,
              backgroundColor:
                selectedPeriod === 'weekly'
                  ? filterButtonActiveColor || '#007AFF'
                  : 'transparent',
            }}
          >
            <Text
              style={{
                fontSize: 14,
                fontWeight: selectedPeriod === 'weekly' ? '600' : '400',
                color:
                  selectedPeriod === 'weekly'
                    ? filterButtonTextActiveColor || '#fff'
                    : filterButtonTextInactiveColor || '#666',
              }}
            >
              Weekly
            </Text>
          </TouchableOpacity>

          <TouchableOpacity
            onPress={() => togglePeriod('monthly')}
            style={{
              paddingHorizontal: 16,
              paddingVertical: 6,
              borderRadius: 4,
              backgroundColor:
                selectedPeriod === 'monthly'
                  ? filterButtonActiveColor || '#007AFF'
                  : 'transparent',
            }}
          >
            <Text
              style={{
                fontSize: 14,
                fontWeight: selectedPeriod === 'monthly' ? '600' : '400',
                color:
                  selectedPeriod === 'monthly'
                    ? filterButtonTextActiveColor || '#fff'
                    : filterButtonTextInactiveColor || '#666',
              }}
            >
              Monthly
            </Text>
          </TouchableOpacity>
        </View>

        {/* Right Arrow */}
        <TouchableOpacity
          onPress={navigateRight}
          style={{
            width: 32,
            height: buttonHeight,
            borderRadius: 6,
            backgroundColor: filterButtonInactiveColor || '#f0f0f0',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <Text
            style={{
              fontSize: 16,
              fontWeight: 'bold',
              color: filterButtonTextInactiveColor || '#666',
            }}
          >
            →
          </Text>
        </TouchableOpacity>
      </View>
    );
  }, [
    showDateFilter,
    isXAxisDate,
    isYAxisDate,
    selectedPeriod,
    filterButtonActiveColor,
    filterButtonInactiveColor,
    filterButtonTextActiveColor,
    filterButtonTextInactiveColor,
    navigateLeft,
    navigateRight,
    togglePeriod,
  ]);

  // Always call useStaticChartData hook - hooks must be called unconditionally
  const staticDataRaw: any = useStaticChartData({
    ...props,
    width,
    height,
    datasetMode:
      props.datasetMode ||
      ((props.datasets && props.datasets.length > 1) ||
      (props.dynamicFields && props.dynamicFields.length > 0)
        ? 'multiple'
        : 'single'),
    attributes: { ...attributes, maxValue },
    componentData,
    // Pass period filter and date offset
    periodFilter: selectedPeriod,
    dateOffset: dateOffset,
  });

  // Only use staticData if we're in canvas mode
  const staticData = isCanvas ? staticDataRaw : null;

  const { setChartValue } = useChartValue(props);

  if (isCanvas) {
    const isZeroValues = allValuesZero(staticData);
    return (
      <View
        style={{
          opacity,
          backgroundColor: backgroundColor,
        }}
      >
        {chartType === 'bar' && (
          <BarChart
            data={staticData}
            {...commonProps}
            showGrid={gridLines?.enabled}
            showValuesOnTopOfBars={true}
            isZeroValues={isZeroValues}
          />
        )}
        {chartType === 'line' && (
          <View style={{ height: height }}>
            <LineChart
              data={staticData}
              {...commonProps}
              withDots={true}
              withShadow={false}
              withScrollableDot={false}
              isZeroValues={isZeroValues}
              maxValue={staticData.maxValue || resolvedMaxValue}
              periodFilter={selectedPeriod}
            >
              {FilterControls}
            </LineChart>
          </View>
        )}
        {chartType === 'pie' && datasetMode === 'fieldList' && (
          <PieChart
            data={(() => {
              const samplePercentages = [45, 35, 20];

              const filteredData = ((items || []) as any[])
                .map((item: any, index: number) => {
                  let labelText = `Item ${index + 1}`;
                  let valueNumber = 0;
                  if (item.label?.text) {
                    labelText = item.label.text;
                  } else if (label?.text && typeof label.text === 'string') {
                    labelText = label.text;
                  }

                  if (item.value?.number) {
                    const val = item.value.number;
                    if (typeof val === 'string') {
                      if (val.includes('>')) {
                        valueNumber =
                          samplePercentages[index % samplePercentages.length] ||
                          0;
                      } else {
                        valueNumber = parseFloat(val) || 0;
                      }
                    } else {
                      valueNumber = parseFloat(val) || 0;
                    }
                  } else if (
                    value?.number &&
                    typeof value.number === 'string'
                  ) {
                    valueNumber = parseFloat(value.number) || 0;
                  }

                  return {
                    name: labelText,
                    value: valueNumber,
                    legendFontColor: textColor || '#7F7F7F',
                    legendFontSize: 15,
                  };
                })
                .filter((item) => item.value > 0);

              return filteredData.map((item, index) => ({
                ...item,
                color:
                  pieUseRandomColors === true
                    ? getSoftColor(index)
                    : getMonochromaticColor(
                        attributes.chartColor || '#8665F4',
                        index,
                        filteredData.length
                      ),
              }));
            })()}
            width={width}
            height={height}
            chartConfig={chartConfig}
            accessor="value"
            backgroundColor="transparent"
            paddingLeft="15"
            center={[0, 0]}
            absolute={false}
            hasLegend={pieShowLegend !== false}
            legendPosition={(pieLegendPosition as 'top' | 'bottom') || 'top'}
            showLabels={pieShowLabels !== false}
            showPercentage={pieShowPercentage !== false}
            showValues={pieShowValues === true}
            labelColor={pieLabelColor || '#000000'}
            labelsOutside={pieLabelsOutside === true}
            isZeroValues={false}
          />
        )}
        {chartType === 'pie' && datasetMode !== 'fieldList' && (
          <PieChart
            data={
              staticData.datasets?.[0]?.data?.map(
                (value: number, index: number) => ({
                  name: staticData.labels?.[index] || `Item ${index + 1}`,
                  value: value,
                  color: staticData.colors?.[index] || getSoftColor(index),
                  legendFontColor: textColor || '#7F7F7F',
                  legendFontSize: 15,
                })
              ) || []
            }
            width={width}
            height={height}
            chartConfig={chartConfig}
            accessor="value"
            backgroundColor="transparent"
            paddingLeft="15"
            center={[0, 0]}
            absolute={false}
            hasLegend={pieShowLegend !== false}
            legendPosition={(pieLegendPosition as 'top' | 'bottom') || 'top'}
            showLabels={pieShowLabels !== false}
            showPercentage={pieShowPercentage !== false}
            showValues={pieShowValues === true}
            labelColor={pieLabelColor || '#000000'}
            labelsOutside={pieLabelsOutside === true}
            isZeroValues={isZeroValues}
          />
        )}
        {chartType === 'radar' && (
          <RadarChart
            data={[]}
            width={width}
            height={height}
            chartConfig={chartConfig}
            accessor="value"
            backgroundColor="transparent"
            paddingLeft="15"
            center={[0, 0]}
            absolute={false}
            hasLegend={hasLegend?.enabled}
            isZeroValues={isZeroValues}
            datasets={staticData.datasets}
            labels={staticData.labels}
            value={0}
          />
        )}
      </View>
    );
  }

  const chartDataRaw: any = useMemo(
    () =>
      mapDataToChartFormat({
        ...props,
        width,
        height,
        datasetMode:
          props.datasetMode ||
          ((props.datasets && props.datasets.length > 1) ||
          (props.dynamicFields && props.dynamicFields.length > 0)
            ? 'multiple'
            : 'single'),
        databaseOptions: props.databaseOptions,
        attributes: { ...attributes, maxValue },
        componentData,
        periodFilter: selectedPeriod,
        dateOffset: dateOffset,
      }),
    [
      props,
      width,
      height,
      attributes,
      maxValue,
      componentData,
      selectedPeriod,
      dateOffset,
      dataBinding,
    ]
  );

  if (!isCanvas) {
    if (!dataBinding || dataBinding.length === 0) {
      return (
        <View
          style={{
            opacity,
            backgroundColor,
            justifyContent: 'center',
            alignItems: 'center',
          }}
        >
          <Text style={{ color: textColor || '#666' }}>
            Loading chart data...
          </Text>
        </View>
      );
    }

    const chartData = chartDataRaw;
    const isZeroValues = allValuesZero(chartData);
    return (
      <View style={{ opacity, backgroundColor }}>
        {FilterControls}
        {chartType === 'bar' && (
          <BarChart
            data={chartData}
            {...commonProps}
            showGrid={gridLines?.enabled}
            showValuesOnTopOfBars={true}
            onDataPointClick={() => {
              //   onPress?.(data);
            }}
            isZeroValues={isZeroValues}
            setChartValue={setChartValue}
          />
        )}
        {chartType === 'line' && (
          <View style={{ height: height }}>
            <LineChart
              data={chartData}
              {...commonProps}
              withDots={true}
              withShadow={false}
              withScrollableDot={false}
              isZeroValues={isZeroValues}
              maxValue={chartData.maxValue || resolvedMaxValue}
              periodFilter={selectedPeriod}
              setChartValue={setChartValue}
            />
          </View>
        )}
        {chartType === 'pie' && datasetMode === 'fieldList' && (
          <>
            {!dataBinding || dataBinding.length === 0 ? (
              <View
                style={{
                  width,
                  height,
                  justifyContent: 'center',
                  alignItems: 'center',
                  backgroundColor: backgroundColor || 'transparent',
                }}
              >
                <Text style={{ color: textColor || '#666', fontSize: 14 }}>
                  No data available
                </Text>
              </View>
            ) : (
              <PieChart
                data={(() => {
                  const filteredData = (dataBinding || [])
                    .map((item: any, index: number) => {
                      let labelText = `Item ${index + 1}`;
                      let valueNumber = 0;
                      if (item['label.text']) {
                        labelText = item['label.text'];
                      } else if (
                        label?.text &&
                        typeof label.text === 'object'
                      ) {
                        const labelBinding = Array.isArray(label.text)
                          ? label.text.find((b: any) => typeof b === 'object')
                          : label.text;
                        if (labelBinding?.source?.fieldId) {
                          labelText =
                            item[labelBinding.source.fieldId] || labelText;
                        }
                      } else if (
                        label?.text &&
                        typeof label.text === 'string'
                      ) {
                        labelText = label.text;
                      } else {
                        const keys = Object.keys(item);
                        if (keys.length > 0) {
                          labelText = item[keys[0]] || labelText;
                        }
                      }

                      if (item['value.number'] !== undefined) {
                        valueNumber = parseFloat(item['value.number']) || 0;
                      } else if (
                        value?.number &&
                        typeof value.number === 'object'
                      ) {
                        const valueBinding = Array.isArray(value.number)
                          ? value.number.find((b: any) => typeof b === 'object')
                          : value.number;
                        if (valueBinding?.source?.fieldId) {
                          valueNumber =
                            parseFloat(item[valueBinding.source.fieldId]) || 0;
                        }
                      } else if (
                        value?.number &&
                        typeof value.number === 'string'
                      ) {
                        valueNumber = parseFloat(value.number) || 0;
                      } else {
                        const keys = Object.keys(item);
                        if (keys.length > 1) {
                          valueNumber = parseFloat(item[keys[1]]) || 0;
                        }
                      }

                      return {
                        name: labelText,
                        value: valueNumber,
                        legendFontColor: textColor || '#7F7F7F',
                        legendFontSize: 15,
                      };
                    })
                    .filter((item) => item.value > 0);

                  return filteredData.map((item, index) => ({
                    ...item,
                    color:
                      pieUseRandomColors === true
                        ? getSoftColor(index)
                        : getMonochromaticColor(
                            (attributes as any).chartColor || '#8665F4',
                            index,
                            filteredData.length
                          ),
                  }));
                })()}
                width={width}
                height={height}
                chartConfig={chartConfig}
                accessor="value"
                backgroundColor="transparent"
                paddingLeft="15"
                center={[0, 0]}
                absolute={false}
                hasLegend={pieShowLegend !== false}
                legendPosition={
                  (pieLegendPosition as 'top' | 'bottom') || 'top'
                }
                showLabels={pieShowLabels !== false}
                showPercentage={pieShowPercentage !== false}
                showValues={pieShowValues === true}
                labelColor={pieLabelColor || '#000000'}
                labelsOutside={pieLabelsOutside === true}
                onDataPointClick={() => {
                  // onPress?.(data);
                }}
                isZeroValues={false}
                setChartValue={setChartValue}
              />
            )}
          </>
        )}
        {chartType === 'pie' && datasetMode !== 'fieldList' && (
          <PieChart
            data={
              chartData.datasets?.[0]?.data?.map(
                (value: number, index: number) => ({
                  name: chartData.labels?.[index] || `Item ${index + 1}`,
                  value: value,
                  color: chartData.colors?.[index] || getSoftColor(index),
                  legendFontColor: textColor || '#7F7F7F',
                  legendFontSize: 15,
                })
              ) || []
            }
            width={width}
            height={height}
            chartConfig={chartConfig}
            accessor="value"
            backgroundColor="transparent"
            paddingLeft="15"
            center={[0, 0]}
            absolute={false}
            hasLegend={pieShowLegend !== false}
            legendPosition={(pieLegendPosition as 'top' | 'bottom') || 'top'}
            showLabels={pieShowLabels !== false}
            showPercentage={pieShowPercentage !== false}
            showValues={pieShowValues === true}
            labelColor={pieLabelColor || '#000000'}
            labelsOutside={pieLabelsOutside === true}
            onDataPointClick={() => {
              // onPress?.(data);
            }}
            isZeroValues={isZeroValues}
            setChartValue={setChartValue}
          />
        )}
        {chartType === 'radar' && (
          <RadarChart
            data={[]}
            width={width}
            height={height}
            chartConfig={chartConfig}
            accessor="value"
            backgroundColor="transparent"
            paddingLeft="15"
            center={[0, 0]}
            absolute={false}
            hasLegend={hasLegend?.enabled}
            onDataPointClick={() => {
              // onPress?.(data);
            }}
            isZeroValues={isZeroValues}
            datasets={chartData.datasets}
            labels={chartData.labels}
            setChartValue={setChartValue}
          />
        )}
      </View>
    );
  }
  return <View />;
};

export default Charts;
